import axios from "../axios-interceptor.js";

const BACKEND_API = process.env.REACT_APP_BACKEND_API;

/**
 * Send OTP for password reset
 * @param {string} email - User's email address
 * @returns {Promise<{success: boolean, message: string, otpToken: string}>}
 */
export const sendPasswordResetOTP = async (email) => {
    let responseObj = { success: false, message: "", otpToken: "" };

    try {
        const res = await axios({
            method: "POST",
            url: `${BACKEND_API}/send-otp`,
            data: {
                email: email.trim(),
                shareToken: ""
            }
        });

        if (res.status === 200) {
            responseObj.success = true;
            responseObj.otpToken = res.data?.result || "";
            responseObj.message = res.data?.message || "OTP sent successfully";
        } else {
            responseObj.message = res.data?.message || "Failed to send OTP";
        }
    } catch (error) {
        responseObj.message = error.response?.data?.message || "Unable to send OTP. Please try again.";
    }

    return responseObj;
};

/**
 * Verify OTP for password reset
 * @param {string} email - User's email address
 * @param {string} otp - 6-digit OTP code
 * @param {string} otpToken - Token received from send-otp response
 * @returns {Promise<{success: boolean, message: string, passwordLength: number|null}>}
 */
export const verifyPasswordResetOTP = async (email, otp, otpToken) => {
    let responseObj = { success: false, message: "", passwordLength: null };

    try {
        const res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/public/form/${otpToken}/verify-otp`,
            data: {
                email: email.trim(),
                purpose: "Password reset",
                // An undefined `otp` is dropped by JSON.stringify rather than
                // sent as null, so the key would vanish from the payload and
                // the backend answers "Data is required can't be null".
                otp: String(otp ?? "").trim()
            }
        });

        // A rejected OTP still comes back as HTTP 200 with a populated `result`
        // (`{ otpVerified: false }`), so a truthy `result` proves nothing — only
        // `otpVerified` says the code was correct.
        const otpVerified = res.data?.result?.otpVerified === true;

        if (otpVerified) {
            responseObj.success = true;
            responseObj.passwordLength = res.data.result?.passwordLength ?? null;
            responseObj.message = res.data?.message || "OTP verified successfully";
        } else {
            responseObj.message = res.data?.message || "Invalid OTP";
        }
    } catch (error) {
        responseObj.message = error.response?.data?.message || "Invalid or expired OTP";
    }

    return responseObj;
};

/**
 * Reset user password
 * @param {string} email - User's email address
 * @param {string} password - New password
 * @param {string} confirmPassword - Password confirmation
 * @param {string} [shareToken] - Token tying the reset to the flow that verified the user
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const resetPassword = async (email, password, confirmPassword, shareToken = "") => {
    let responseObj = { success: false, message: "" };

    try {
        const res = await axios({
            method: "PUT",
            url: `${BACKEND_API}/reset`,
            data: {
                email: email.trim(),
                // Coerced, never trimmed: an undefined value would be dropped by
                // JSON.stringify and the key would vanish from the payload, while
                // spaces are legitimate password characters.
                password: password ?? "",
                confirmPassword: confirmPassword ?? "",
                shareToken: shareToken || ""
            }
        });

        if (res.status === 200) {
            responseObj.success = true;
            responseObj.message = res.data?.message || "Password updated successfully";
        } else {
            responseObj.message = res.data?.message || "Failed to reset password";
        }
    } catch (error) {
        responseObj.message = error.response?.data?.message || "Unable to reset password. Please try again.";
    }

    return responseObj;
};

/**
 * Create password for new user (using invitation token from email)
 * @param {string} token - Invitation token from email link
 * @param {string} password - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const createPassword = async (token, password, confirmPassword) => {
    let responseObj = { success: false, message: "" };

    try {
        // TODO: Update endpoint when backend API is provided
        const res = await axios({
            method: "POST",
            url: `${BACKEND_API}/create-password`,
            data: {
                token: token,
                password: password,
                confirmPassword: confirmPassword
            }
        });

        if (res.status === 200) {
            responseObj.success = true;
            responseObj.message = res.data?.message || "Password created successfully";
        } else {
            responseObj.message = res.data?.message || "Failed to create password";
        }
    } catch (error) {
        responseObj.message = error.response?.data?.message || "Unable to create password. Please try again.";
    }

    return responseObj;
};

/**
 * Validate invitation token and get user email
 * @param {string} token - Invitation token from email link
 * @returns {Promise<{success: boolean, message: string, email: string, passwordLength: number|null}>}
 */
export const validateToken = async (token) => {
    let responseObj = { success: false, message: "", email: "", passwordLength: null };

    try {
        const res = await axios({
            method: "GET",
            url: `${BACKEND_API}/validate-token`,
            params: {
                token: token
            }
        });

        if (res.status === 200 && res.data?.result) {
            responseObj.success = true;
            responseObj.email = res.data.result.email || res.data.result;
            responseObj.passwordLength = res.data.result?.passwordLength ?? null;
            responseObj.message = res.data?.message || "Token validated successfully";
        } else {
            responseObj.message = res.data?.message || "Invalid or expired token";
        }
    } catch (error) {
        responseObj.message = error.response?.data?.message || "Invalid or expired token";
    }

    return responseObj;
};

const authServices = {
    sendPasswordResetOTP,
    verifyPasswordResetOTP,
    resetPassword,
    createPassword,
    validateToken
};

export default authServices;
